import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { Container } from "../components/ui-kit";
import { ShieldCheck, ShieldX, Search, Download } from "lucide-react";
import { Input } from "../components/ui/input";
import { LOGO_URL } from "../lib/api";

export default function VerifyReceipt() {
  const { receiptNumber } = useParams();
  const navigate = useNavigate();
  const [num, setNum] = useState(receiptNumber || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async (n) => {
    if (!n) return;
    setLoading(true); setResult(null);
    try {
      const { data } = await api.get(`/receipts/verify/${n}`);
      setResult(data);
    } catch { setResult({ valid: false, message: "No verified receipt found for this reference." }); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (receiptNumber) check(receiptNumber); }, [receiptNumber]);

  return (
    <div className="min-h-[70vh] bg-sand">
      <Container className="py-20">
        <div className="mx-auto max-w-xl">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Helping Hands" className="h-12 w-12 rounded-full object-cover ring-1 ring-gold/30" />
            <div>
              <h1 className="font-display text-3xl font-bold text-emerald-900">Receipt Verification</h1>
              <p className="text-sm text-slate-500">Confirm the authenticity of a Helping Hands receipt.</p>
            </div>
          </div>

          <div className="mt-8 flex gap-2">
            <Input value={num} onChange={(e) => setNum(e.target.value)} placeholder="e.g. HH-2026-000001" data-testid="verify-input" className="bg-white" />
            <button onClick={() => { navigate(`/verify-receipt/${num}`); check(num); }} data-testid="verify-btn" className="inline-flex items-center gap-2 rounded-lg bg-emerald-900 px-5 text-sm font-bold text-white hover:bg-emerald-800"><Search className="h-4 w-4" /> Verify</button>
          </div>

          {loading && <p className="mt-8 text-center text-slate-400">Verifying…</p>}

          {result && (
            <div className="mt-8 rounded-3xl border border-emerald-900/10 bg-white p-8" data-testid="verify-result">
              {result.valid ? (
                <>
                  <div className="flex items-center gap-3 text-emerald-700"><ShieldCheck className="h-8 w-8" /><span className="font-display text-2xl font-bold">Verified</span></div>
                  <div className="mt-6 space-y-3 text-sm">
                    {[["Receipt Number", result.receipt_number], ["Date", result.date], ["Amount", `${result.currency} ${Number(result.amount).toLocaleString()}`], ["Program", result.program], ["Status", result.status]].map(([l, v]) => (
                      <div key={l} className="flex justify-between border-b border-emerald-900/5 pb-2"><span className="text-slate-500">{l}</span><span className="font-semibold text-emerald-900">{v}</span></div>
                    ))}
                  </div>
                  {result.test_mode && <div className="mt-4 rounded-lg bg-gold-soft px-3 py-2 text-xs font-semibold text-emerald-900">This receipt was generated in TEST MODE.</div>}
                  <a href={`${API}/receipts/${result.receipt_number}/pdf`} target="_blank" rel="noreferrer" data-testid="verify-download-pdf" className="mt-5 flex items-center justify-center gap-2 rounded-full bg-emerald-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-800"><Download className="h-4 w-4" /> Download PDF Receipt</a>
                  <p className="mt-4 text-xs text-slate-400">For privacy, supporter personal details are never shown on this public page.</p>
                </>
              ) : (
                <div className="flex flex-col items-center gap-3 py-6 text-center">
                  <ShieldX className="h-10 w-10 text-red-500" />
                  <p className="font-display text-xl font-bold text-emerald-900">Not Verified</p>
                  <p className="text-sm text-slate-500">{result.message}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}
